/**
 * AuditLogTab — Phase 1
 * =====================
 * Admin Settings → Audit Log tab. Lists AuditEntry rows for the
 * current tenant with filters, pagination, and CSV export.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Shield, Search, Download, ChevronLeft, ChevronRight, RefreshCw,
} from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

// Mirror of the AuditAction enum. Kept here so the filter dropdown
// stays usable even when the server returns no rows.
const ACTIONS = [
  'USER_LOGIN', 'USER_LOGIN_FAILED', 'USER_LOGOUT',
  'PASSWORD_CHANGED', 'PASSWORD_RESET_REQUESTED', 'PASSWORD_RESET_COMPLETED',
  'MFA_ENABLED', 'MFA_DISABLED',
  'INVESTIGATION_CREATED', 'INVESTIGATION_UPDATED', 'INVESTIGATION_DELETED',
  'CASE_SHARED', 'ASSOCIATION_CREATED', 'ASSOCIATION_DELETED', 'EVIDENCE_EXPORTED',
  'DOOR_UNLOCKED', 'DOOR_LOCKED', 'DOOR_HELD_OPEN', 'DOOR_RELEASE_HOLD',
  'LOCKDOWN_ACTIVATED', 'LOCKDOWN_DEACTIVATED',
  'INTEGRATION_ADDED', 'INTEGRATION_UPDATED', 'INTEGRATION_REMOVED', 'INTEGRATION_TESTED',
  'USER_CREATED', 'USER_UPDATED', 'USER_DELETED', 'ROLE_CHANGED', 'SETTINGS_UPDATED',
  'OTHER',
];

const SEVERITIES = ['All', 'INFO', 'WARNING', 'CRITICAL'];

const RANGE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: 'custom', label: 'Custom' },
];

function startOf(rangeId) {
  const now = new Date();
  if (rangeId === 'today') {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  if (rangeId === '7d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  }
  if (rangeId === '30d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    return d.toISOString();
  }
  return null;
}

function severityClass(s) {
  switch (s) {
    case 'CRITICAL': return 'audit-badge audit-badge-critical';
    case 'WARNING':  return 'audit-badge audit-badge-warning';
    default:         return 'audit-badge audit-badge-info';
  }
}

function fmtTs(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch (_) {
    return String(iso);
  }
}

function csvEscape(v) {
  const s = v === null || v === undefined ? '' : String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsv(rows) {
  const headers = [
    'Timestamp', 'User Name', 'User Email', 'Action', 'Severity',
    'Resource Type', 'Resource Name', 'Description', 'IP Address',
  ];
  const lines = [headers.map(csvEscape).join(',')];
  for (const r of rows) {
    lines.push([
      r.createdAt ? new Date(r.createdAt).toISOString() : '',
      r.userName || '',
      r.userEmail || '',
      r.action || '',
      r.severity || '',
      r.resourceType || '',
      r.resourceName || '',
      r.description || '',
      r.ipAddress || '',
    ].map(csvEscape).join(','));
  }
  return lines.join('\n');
}

export default function AuditLogTab() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit] = useState(50);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [action, setAction] = useState('');
  const [severity, setSeverity] = useState('All');
  const [range, setRange] = useState('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce search
  const searchTimer = useRef(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [searchInput]);

  const dateRange = useMemo(() => {
    if (range === 'custom') {
      return {
        startDate: customStart || null,
        endDate: customEnd || null,
      };
    }
    return {
      startDate: startOf(range),
      endDate: null,
    };
  }, [range, customStart, customEnd]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (action) params.set('action', action);
      if (severity && severity !== 'All') params.set('severity', severity);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (search) params.set('search', search);

      const res = await fetchWithTenant(`/api/audit?${params.toString()}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setEntries(Array.isArray(data.entries) ? data.entries : []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) {
      setError(e.message || 'Failed to load audit log');
      setEntries([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, limit, action, severity, dateRange, search]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = useCallback(() => {
    if (!entries.length) return;
    const csv = buildCsv(entries);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [entries]);

  return (
    <div className="audit-tab">
      <div className="audit-header">
        <h2 className="audit-title"><Shield size={18} /> Audit Log</h2>
        <p className="audit-subtitle">
          {total > 0
            ? `Showing ${entries.length} of ${total} entries`
            : 'Tenant-scoped activity log for security review.'}
        </p>
      </div>

      {/* Filters */}
      <div className="audit-filters">
        <div className="audit-filter-row">
          <select
            className="audit-select"
            value={action}
            onChange={(e) => { setAction(e.target.value); setPage(1); }}
          >
            <option value="">All actions</option>
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>

          <div className="audit-chips">
            {SEVERITIES.map((s) => (
              <button
                key={s}
                type="button"
                className={`audit-chip ${severity === s ? 'active' : ''}`}
                onClick={() => { setSeverity(s); setPage(1); }}
              >
                {s}
              </button>
            ))}
          </div>

          <select
            className="audit-select"
            value={range}
            onChange={(e) => { setRange(e.target.value); setPage(1); }}
          >
            {RANGE_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>

          {range === 'custom' && (
            <>
              <input
                type="date"
                className="audit-input"
                value={customStart}
                onChange={(e) => { setCustomStart(e.target.value); setPage(1); }}
              />
              <input
                type="date"
                className="audit-input"
                value={customEnd}
                onChange={(e) => { setCustomEnd(e.target.value); setPage(1); }}
              />
            </>
          )}

          <div className="audit-search">
            <Search size={14} />
            <input
              type="text"
              className="audit-input audit-search-input"
              placeholder="Search user, resource, description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={exportCsv} disabled={!entries.length}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="audit-table-wrap">
        {error && <div className="audit-error">{error}</div>}
        {loading && <div className="audit-loading">Loading audit log...</div>}
        {!loading && !error && entries.length === 0 && (
          <div className="audit-empty">No audit entries match the current filters.</div>
        )}
        {!loading && entries.length > 0 && (
          <table className="audit-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Resource</th>
                <th>Description</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => (
                <tr key={row.id}>
                  <td className="audit-ts">{fmtTs(row.createdAt)}</td>
                  <td>
                    <div className="audit-user-name">{row.userName || '—'}</div>
                    {row.userEmail && <div className="audit-user-email">{row.userEmail}</div>}
                  </td>
                  <td>
                    <span className={severityClass(row.severity)}>{row.action}</span>
                  </td>
                  <td>
                    {row.resourceType && <div className="audit-res-type">{row.resourceType}</div>}
                    {row.resourceName && <div className="audit-res-name">{row.resourceName}</div>}
                  </td>
                  <td className="audit-desc">{row.description || ''}</td>
                  <td className="audit-ip">{row.ipAddress || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="audit-pagination">
          <button
            className="btn btn-secondary btn-sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <span className="audit-page-info">Page {page} of {pages}</span>
          <button
            className="btn btn-secondary btn-sm"
            disabled={page >= pages || loading}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
